import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button,
  Chip,
  Link,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  LinearProgress
} from '@mui/material';
import { 
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { fetchVideoById, processVideo, cancelVideoProcessing, VideoStatus } from '../store/slices/videoSlice';
import { format } from 'date-fns';
import VideoComparison from '../components/VideoComparison';
import apiService from '../services/api';
import axios from 'axios';
import { useProcessingStatus } from '../hooks/useProcessingStatus';

// Helper to verify if video file exists in storage
const verifyVideoStorage = async (video: any): Promise<{ exists: boolean; error?: string }> => {
  if (!video || !video.original_url) {
    console.warn(`Cannot verify storage: Missing video URL for video ID ${video?.id}`);
    return { exists: false, error: 'Missing video URL' };
  }
  
  try {
    // Determine which bucket to check
    const bucket = video.storage_bucket || 'original-videos';
    const fileName = video.original_url;
    
    // Extract base filename without timestamp if needed
    const baseFileName = fileName.includes('-') 
      ? fileName.replace(/^\d+-\d+-(.+)$/, '$1') 
      : fileName;
    
    console.log(`Verifying storage for video ${video.id} in bucket ${bucket}, file: ${fileName} (base: ${baseFileName})`);
    
    // Encode the filename for URL safety
    const encodedFilename = encodeURIComponent(fileName);
    
    // Get auth token from localStorage if available
    const token = localStorage.getItem('authToken');
    
    // Prepare headers with auth token when available
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Use the verify endpoint to check if the file exists
    // This is a public endpoint in development mode but we still include auth token when available
    const verifyUrl = `/api/storage/verify/${bucket}/${encodedFilename}`;
    console.log(`Using verify endpoint: ${verifyUrl}`);
    
    // Make a HEAD request to check if the file exists
    const response = await fetch(verifyUrl, {
      method: 'HEAD',
      headers
    });
    
    const status = response.status;
    console.log(`Verify request status: ${status}`);
    
    if (status === 200) {
      console.log('File exists in storage');
      return { exists: true };
    }
    
    if (status === 404) {
      console.log('File not found in storage, trying with base filename');
      
      // If the file wasn't found, try with just the base filename
      const encodedBaseFilename = encodeURIComponent(baseFileName);
      
      if (encodedBaseFilename !== encodedFilename) {
        // Only try with base filename if it's different from the original
        const newVerifyUrl = `/api/storage/verify/${bucket}/${encodedBaseFilename}`;
        console.log(`Using new verify endpoint: ${newVerifyUrl}`);
        
        const newResponse = await fetch(newVerifyUrl, {
          method: 'HEAD',
          headers
        });
        
        if (newResponse.status === 200) {
          console.log('File exists using base filename');
          return { exists: true };
        }
      }
      
      // If we've tried both options and both failed:
      return { exists: false, error: 'File not found in storage' };
    }
    
    // Any other status code indicates a server error
    return { 
      exists: false, 
      error: `Unable to verify file exists (status: ${status})` 
    };
    
  } catch (err: any) {
    console.error('Error checking file storage:', err);
    return { exists: false, error: err.message };
  }
};

const VideoDetails: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // State for showing notifications
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Use the processing status hook for real-time updates
  const {
    status: processingStatus,
    isPolling,
    startPolling,
    stopPolling
  } = useProcessingStatus(videoId, false);
  
  // Get video from Redux store
  const { selectedVideo, loading, error } = useSelector((state: RootState) => state.videos);
  
  // State for storage verification
  const [storageError, setStorageError] = useState<string | null>(null);
  
  // Fetch video details on component mount
  useEffect(() => {
    if (videoId) {
      dispatch(fetchVideoById(videoId));
    }
  }, [dispatch, videoId]);
  
  // Start polling when video is in processing state
  useEffect(() => {
    if (selectedVideo?.status === 'Processing' && !isPolling) {
      startPolling();
    } else if (selectedVideo?.status !== 'Processing' && isPolling) {
      stopPolling();
    }
    
    // If processing status shows completion but our Redux store hasn't updated
    if (processingStatus?.isComplete && selectedVideo?.status === 'Processing') {
      dispatch(fetchVideoById(videoId as string));
    }
  }, [selectedVideo, processingStatus, isPolling, startPolling, stopPolling, dispatch, videoId]);
  
  // Check storage when selected video changes
  useEffect(() => {
    const checkStorage = async () => {
      if (selectedVideo) {
        console.log('Checking storage for video:', selectedVideo.id);
        const storageResult = await verifyVideoStorage(selectedVideo);
        
        // Only set storage error if file doesn't exist
        if (!storageResult.exists) {
          console.warn('Storage verification failed:', storageResult.error);
          setStorageError(storageResult.error || 'Unknown storage error');
        } else {
          setStorageError(null);
        }
      }
    };
    
    checkStorage();
  }, [selectedVideo]);
  
  // Function to handle video processing
  const handleProcessVideo = async () => {
    if (!videoId) return;
    
    try {
      // Call the process video API
      await apiService.processVideo(videoId);
      
      // Show notification
      setNotification({
        open: true,
        message: 'Video processing started',
        severity: 'success'
      });
      
      // Start polling for status updates
      startPolling();
      
      // Refresh video details
      dispatch(fetchVideoById(videoId));
    } catch (error) {
      console.error('Error processing video:', error);
      setNotification({
        open: true,
        message: 'Failed to start video processing',
        severity: 'error'
      });
    }
  };
  
  // Function to handle cancellation of video processing
  const handleCancelProcessing = async () => {
    if (!videoId) return;
    
    try {
      // Dispatch the cancel action
      await dispatch(cancelVideoProcessing(videoId));
      
      // Show notification
      setNotification({
        open: true,
        message: 'Video processing cancelled',
        severity: 'info'
      });
      
      // Refresh video details
      dispatch(fetchVideoById(videoId));
    } catch (error) {
      console.error('Error cancelling video processing:', error);
      setNotification({
        open: true,
        message: 'Failed to cancel video processing',
        severity: 'error'
      });
    }
  };
  
  // Function to handle notification close
  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };
  
  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPp');
    } catch (e) {
      return 'Invalid date';
    }
  };
  
  // Function to get URL for video playback
  const getVideoUrl = (video: any) => {
    if (!video) return null;
    
    // Check if we have a URL - use it exactly as stored in the database
    if (!video.original_url) {
      console.warn('Missing original_url for video');
      return null;
    }
    
    // If it's already a complete URL, use it directly with proxy
    if (video.original_url.startsWith('http')) {
      console.log(`Using direct URL from DB: ${video.original_url}`);
      return `/api/direct-videos/proxy?url=${encodeURIComponent(video.original_url)}`;
    }
    
    // Otherwise construct API URL using the exact path/filename from DB
    // This is a public endpoint in development mode - use the complete path including userId and timestamp
    const bucket = video.storage_bucket || 'original-videos';
    return `/api/storage/${bucket}/${encodeURIComponent(video.original_url)}`;
  };
  
  // Get processed video URL if available
  const getProcessedVideoUrl = (video: any) => {
    if (!video || !video.processed_url) return null;
    
    // If it's already a complete URL, use it directly with proxy
    if (video.processed_url.startsWith('http')) {
      console.log(`Using direct processed URL from DB: ${video.processed_url}`);
      return `/api/videos/proxy?url=${encodeURIComponent(video.processed_url)}`;
    }
    
    // Otherwise use the API route with the processed bucket
    const bucket = video.processed_bucket || 'processed-videos';
    return `/api/storage/${bucket}/${encodeURIComponent(video.processed_url)}`;
  };
  
  // Determine if the Process button should be disabled
  const isProcessButtonDisabled = () => {
    if (!selectedVideo) return true;
    if (processingStatus?.isProcessing) return true;
    return false;
  };
  
  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={() => dispatch(fetchVideoById(videoId as string))}
        >
          Retry
        </Button>
      </Box>
    );
  }
  
  // Render if video not found
  if (!selectedVideo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Video not found
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/videos')}
        >
          Back to Video Management
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Video header section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" component="h1">
          {selectedVideo.title || 'Untitled Video'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<EditIcon />}
            onClick={() => navigate(`/videos/${videoId}/edit`)}
          >
            Edit
          </Button>
          
          {/* Show either Process Video or Cancel Processing button based on status */}
          {processingStatus?.isProcessing || selectedVideo.status === 'Processing' ? (
            <Button 
              variant="contained" 
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelProcessing}
            >
              Cancel Processing
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={handleProcessVideo}
              disabled={isProcessButtonDisabled()}
            >
              Process Video
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Status chip */}
      <Box sx={{ mb: 3 }}>
        <Chip 
          label={processingStatus?.status || selectedVideo.status} 
          color={
            (processingStatus?.isComplete || selectedVideo.status === VideoStatus.PreviewReady || selectedVideo.status === VideoStatus.Published) ? 'success' : 
            (processingStatus?.isProcessing || selectedVideo.status === VideoStatus.Processing) ? 'warning' :
            (processingStatus?.hasFailed) ? 'error' : 'primary'
          } 
          sx={{ fontWeight: 'bold' }}
        />
        
        {processingStatus?.isProcessing && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Processing in progress... This may take a few minutes.
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Storage error alert if any */}
      {storageError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {storageError}
        </Alert>
      )}
      
      {/* Video details grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Original Video
            </Typography>
            {selectedVideo.original_url ? (
              <Box component="video" controls width="100%" sx={{ borderRadius: 1 }} src={getVideoUrl(selectedVideo)} />
            ) : (
              <Alert severity="info">No original video available</Alert>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processed Video
            </Typography>
            {getProcessedVideoUrl(selectedVideo) ? (
              <Box component="video" controls width="100%" sx={{ borderRadius: 1 }} src={getProcessedVideoUrl(selectedVideo)} />
            ) : (
              <Box sx={{ 
                height: '200px', 
                bgcolor: 'grey.100', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2
              }}>
                <Typography color="text.secondary">
                  {processingStatus?.isProcessing ? 'Processing in progress...' : 'No processed version yet'}
                </Typography>
                {processingStatus?.isProcessing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={30} />
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelProcessing}
                      sx={{ mt: 1 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleProcessVideo}
                    disabled={isProcessButtonDisabled()}
                  >
                    Process Video
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Video metadata */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Video Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Title</Typography>
            <Typography variant="body1">{selectedVideo.title || 'Untitled'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
            <Typography variant="body1">{formatDate(selectedVideo.created_at)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Typography variant="body1">{processingStatus?.status || selectedVideo.status}</Typography>
          </Grid>
          {selectedVideo.metadata?.duration && (
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
              <Typography variant="body1">
                {Math.floor(selectedVideo.metadata.duration / 60)}:{(selectedVideo.metadata.duration % 60).toString().padStart(2, '0')}
              </Typography>
            </Grid>
          )}
          {selectedVideo.description && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Description</Typography>
              <Typography variant="body1">{selectedVideo.description}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      {/* Processing details if available */}
      {processingStatus?.processing_details && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Processing Details
          </Typography>
          <Grid container spacing={2}>
            {processingStatus.processing_details.processing_started_at && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Started At</Typography>
                <Typography variant="body1">{formatDate(processingStatus.processing_details.processing_started_at)}</Typography>
              </Grid>
            )}
            {processingStatus.processing_details.processing_completed_at && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Completed At</Typography>
                <Typography variant="body1">{formatDate(processingStatus.processing_details.processing_completed_at)}</Typography>
              </Grid>
            )}
            {processingStatus.processing_details.progress !== undefined && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Progress</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={processingStatus.processing_details.progress * 100} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(processingStatus.processing_details.progress * 100)}%
                  </Typography>
                </Box>
              </Grid>
            )}
            {processingStatus.processing_details.warnings && processingStatus.processing_details.warnings.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Warnings</Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {processingStatus.processing_details.warnings.map((warning, index) => (
                    <Typography component="li" variant="body2" key={index}>{warning}</Typography>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideoDetails; 
